/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * OrgRichText
 * -----------
 * A description field with a formatting toolbar, for the two places an
 * organizer writes more than a line: the event description and an activity's.
 *
 * It keeps `OrgTextArea`'s contract exactly — same `OrgField` wrapper, same
 * label / value / onChange / required / error / help props, same live character
 * count — so the wizard's layout, labels and accessibility wiring are unchanged
 * by the swap. The count measures words, not markup: tags are not something the
 * organizer typed, and counting them would shrink the budget every time they
 * made something bold.
 *
 * Typography is a FIXED SET of tokens, never a font picker. The toolbar can
 * produce eight class names and nothing else, `sanitizeRichText` allows those
 * eight and nothing else, and `eventDetail.css` decides what each one looks
 * like. An organizer can make a BIB number stand out; an organizer cannot put
 * lime green Impact on a public page, and the site can restyle every stored
 * description at once without touching a row.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { Mark, mergeAttributes } from '@tiptap/core';
import OrgField from './OrgField';
// The same eight token rules the public event page loads, from the same file.
import '../../../styles/richText.css';
import {
  sanitizeRichText, richTextToPlain, looksLikeHtml,
  FONT_CLASSES, SIZE_CLASSES, COLOR_CLASSES,
} from '../../../utils/sanitizeRichText';

/**
 * One mark per token group, so a choice REPLACES the previous one in its group
 * rather than stacking: picking Large after X-Large leaves one size, not two
 * fighting spans. Across groups they combine, which is the whole point — a BIB
 * number is mono AND large AND accent.
 */
function tokenMark(name, classes) {
  return Mark.create({
    name,
    // Marks of the same group must not nest; TipTap replaces instead.
    excludes: name,
    /**
     * Not inclusive: typing immediately after a token does NOT continue it.
     *
     * The default is the opposite, and it reads as a bug. An organizer sets
     * "BIB 4821" in mono, types " — collect at Gate 2" straight after, and the
     * whole sentence comes out mono too. Bold behaves that way because bold is
     * a run of emphasis you are still inside; a typeface token marks one
     * specific thing, and the thing has ended.
     */
    inclusive: false,
    addAttributes() {
      return {
        token: {
          default: null,
          parseHTML: (element) => classes.find((c) => element.classList.contains(c)) || null,
          renderHTML: (attrs) => (attrs.token ? { class: attrs.token } : {}),
        },
      };
    },
    parseHTML() {
      return classes.map((c) => ({ tag: `span.${c}` }));
    },
    renderHTML({ HTMLAttributes }) {
      return ['span', mergeAttributes(HTMLAttributes), 0];
    },
    addCommands() {
      return {
        [`set${name}`]: (token) => ({ commands }) => (
          token ? commands.setMark(name, { token }) : commands.unsetMark(name)
        ),
      };
    },
  });
}

const FontToken = tokenMark('fontToken', FONT_CLASSES);
const SizeToken = tokenMark('sizeToken', SIZE_CLASSES);
const ColorToken = tokenMark('colorToken', COLOR_CLASSES);

const FONT_OPTIONS = [
  { value: '', label: 'Default type' },
  { value: 'rt-serif', label: 'Serif' },
  { value: 'rt-mono', label: 'Mono — for codes' },
];
const SIZE_OPTIONS = [
  { value: '', label: 'Default size' },
  { value: 'rt-sm', label: 'Small' },
  { value: 'rt-lg', label: 'Large' },
  { value: 'rt-xl', label: 'X-Large' },
];
const COLOR_OPTIONS = [
  { value: '', swatch: '#453C35', title: 'Default ink' },
  { value: 'rt-accent', swatch: '#C2410C', title: 'Accent' },
  { value: 'rt-muted', swatch: '#8a7f76', title: 'Muted' },
  { value: 'rt-go', swatch: '#15803D', title: 'Go green' },
];

/** A stored value the editor can open: legacy plain text becomes one paragraph. */
function toEditorContent(value) {
  if (!value) return '';
  if (looksLikeHtml(value)) return sanitizeRichText(value);
  // Plain text from before the toolbar existed. Its newlines were already
  // collapsed on the detail page, so a single paragraph is what it has always
  // looked like to a visitor.
  return `<p>${value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`;
}

function ToolbarButton({ active, onClick, title, children }) {
  return (
    <button
      type="button"
      className={`orgf-rt__btn${active ? ' is-on' : ''}`}
      title={title}
      aria-label={title}
      aria-pressed={!!active}
      // The editor must keep the selection the click is about to format.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export default function OrgRichText({
  label, value, onChange, required, error, help, placeholder, maxLength,
}) {
  // The last value this component handed up. Anything else arriving as `value`
  // came from outside and is the only thing worth rebuilding the document for.
  const emitted = useRef(null);

  /**
   * `onUpdate` is installed once, so it must not close over a stale `onChange`.
   * The ref is what keeps the editor's options stable AND the callback current.
   */
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  /**
   * Built once, deliberately.
   *
   * An inline array here means a NEW set of extension instances on every
   * render, and `useEditor` reapplies options when they change — so every
   * keystroke reconfigured the editor, which re-rendered, which reconfigured
   * it again. React caught it as "Maximum update depth exceeded". It only
   * surfaced under sustained editing, which is why the unit tests never saw it
   * and the browser did.
   */
  const extensions = useMemo(() => [
    StarterKit.configure({
      // Headings below h3 would outrank the page's own section headings.
      heading: { levels: [3] },
      // Nothing in a description needs a code block or a horizontal rule, and
      // every extra mark is another thing the sanitizer has to allow.
      codeBlock: false,
      horizontalRule: false,
      blockquote: false,
      link: false,
    }),
    Underline,
    Link.configure({ openOnClick: false, autolink: false }),
    FontToken, SizeToken, ColorToken,
  ], []);

  const editorProps = useMemo(() => ({
    attributes: {
      class: 'orgf-rt__area',
      ...(placeholder ? { 'data-placeholder': placeholder } : {}),
    },
    // Paste is the other way markup gets in, and the way most of it does.
    transformPastedHTML: (html) => sanitizeRichText(html),
  }), [placeholder]);

  // The empty deps array matters as much as the options: it tells useEditor to
  // build the editor once rather than reconciling it on every render.
  const editor = useEditor({
    extensions,
    editorProps,
    content: toEditorContent(value),
    onUpdate: ({ editor: instance }) => {
      const html = instance.getHTML();
      // TipTap's empty document is "<p></p>". Storing that would make a blank
      // field look filled to every `value &&` check in the codebase.
      const next = instance.isEmpty ? '' : sanitizeRichText(html);
      // A transaction that leaves the stored string identical is not an edit.
      // Dispatching anyway marks the form dirty and re-renders the whole
      // wizard for nothing, and every one of those is a chance for the parent
      // to hand back a value that starts the cycle again.
      if (next === emitted.current) return;
      emitted.current = next;
      onChangeRef.current(next);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * An edit loaded after mount, or a form reset, has to reach the editor. What
   * must NOT reach it is the editor's own output coming back round through the
   * parent — so the last value this component emitted is remembered, and an
   * incoming value equal to it is ignored.
   *
   * Comparing the strings instead looks equivalent and is not. A legacy
   * description is stored as plain text and opens as `<p>that text</p>`, so the
   * two never match, `setContent` ran on every render, and that rebuilt the
   * document under the caret: React hit "Maximum update depth exceeded", and an
   * organizer applying mono then a size watched the mono quietly disappear
   * because the selection had been destroyed between the two clicks.
   */
  useEffect(() => {
    if (!editor) return;
    const incoming = value || '';
    if (incoming === emitted.current) return;
    emitted.current = incoming;
    editor.commands.setContent(toEditorContent(incoming), { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const length = useMemo(() => richTextToPlain(value || '').length, [value]);
  const over = maxLength != null && length > maxLength;

  const token = (markName) => editor?.getAttributes(markName)?.token || '';
  const setToken = (command, next) => {
    if (!editor) return;
    editor.chain().focus()[command](next || null).run();
  };

  return (
    <OrgField label={label} required={required} error={error} help={help}>
      {({ id, invalid, describedBy }) => (
        <>
          <div className={`orgf-rt${invalid ? ' is-invalid' : ''}`}>
            <div className="orgf-rt__bar" role="toolbar" aria-label={`${label} formatting`}>
              <select
                className="orgf-rt__sel"
                title="Text style"
                aria-label="Text style"
                value={editor?.isActive('heading', { level: 3 }) ? 'h3' : 'p'}
                onChange={(event) => {
                  if (!editor) return;
                  const chain = editor.chain().focus();
                  if (event.target.value === 'h3') chain.setNode('heading', { level: 3 }).run();
                  else chain.setParagraph().run();
                }}
              >
                <option value="p">Body text</option>
                <option value="h3">Heading</option>
              </select>

              <select
                className="orgf-rt__sel" title="Typeface" aria-label="Typeface"
                value={token('fontToken')}
                onChange={(event) => setToken('setfontToken', event.target.value)}
              >
                {FONT_OPTIONS.map((o) => <option key={o.label} value={o.value}>{o.label}</option>)}
              </select>

              <select
                className="orgf-rt__sel" title="Size" aria-label="Size"
                value={token('sizeToken')}
                onChange={(event) => setToken('setsizeToken', event.target.value)}
              >
                {SIZE_OPTIONS.map((o) => <option key={o.label} value={o.value}>{o.label}</option>)}
              </select>

              <span className="orgf-rt__sw">
                {COLOR_OPTIONS.map((o) => (
                  <button
                    key={o.title}
                    type="button"
                    className={`orgf-rt__dot${token('colorToken') === o.value ? ' is-on' : ''}`}
                    style={{ background: o.swatch }}
                    title={o.title}
                    aria-label={o.title}
                    aria-pressed={token('colorToken') === o.value}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => setToken('setcolorToken', o.value)}
                  />
                ))}
              </span>

              <span className="orgf-rt__sep" />

              <ToolbarButton
                title="Bold" active={editor?.isActive('bold')}
                onClick={() => editor?.chain().focus().toggleBold().run()}
              ><b>B</b></ToolbarButton>
              <ToolbarButton
                title="Italic" active={editor?.isActive('italic')}
                onClick={() => editor?.chain().focus().toggleItalic().run()}
              ><i>I</i></ToolbarButton>
              <ToolbarButton
                title="Underline" active={editor?.isActive('underline')}
                onClick={() => editor?.chain().focus().toggleUnderline().run()}
              ><u>U</u></ToolbarButton>
              <ToolbarButton
                title="Strikethrough" active={editor?.isActive('strike')}
                onClick={() => editor?.chain().focus().toggleStrike().run()}
              ><s>S</s></ToolbarButton>

              <span className="orgf-rt__sep" />

              <ToolbarButton
                title="Bulleted list" active={editor?.isActive('bulletList')}
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
              >• List</ToolbarButton>
              <ToolbarButton
                title="Numbered list" active={editor?.isActive('orderedList')}
                onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              >1. List</ToolbarButton>

              <span className="orgf-rt__sep" />

              <ToolbarButton
                title="Link" active={editor?.isActive('link')}
                onClick={() => {
                  if (!editor) return;
                  if (editor.isActive('link')) {
                    editor.chain().focus().unsetLink().run();
                    return;
                  }
                  // eslint-disable-next-line no-alert
                  const url = window.prompt('Link to:', 'https://');
                  if (url) editor.chain().focus().setLink({ href: url }).run();
                }}
              >Link</ToolbarButton>
              <ToolbarButton
                title="Clear formatting"
                onClick={() => editor?.chain().focus().unsetAllMarks().clearNodes().run()}
              >Clear</ToolbarButton>
            </div>

            <EditorContent
              editor={editor}
              id={id}
              aria-invalid={invalid}
              aria-describedby={describedBy}
            />
            {/* Inside the box, not floated beside the label: a float there
                narrowed the editor and left the count hanging off its edge. */}
            {maxLength != null && (
              <div className="orgf-rt__foot">
                <span className={`orgf-count${over ? ' over' : ''}`}>{length}/{maxLength}</span>
              </div>
            )}
          </div>
        </>
      )}
    </OrgField>
  );
}
